// Copyright 2025, University of Maryland, All Rights Reserved
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { LucideBot, LucideFileText } from "lucide-react"
import { useState, useEffect } from "react"
import { type PromptConfig } from "@/api/types/config"
import { LLMModelSelect } from "@umd-dit-ai-workspace/workspaces-component"
import type { ConfigComponentProps } from '../ConfigForm'

const DEFAULT_PROMPT_CONFIG: PromptConfig = {
  prompt_text: '',
  json_schema: {},
  model_name: '' // Will be set when user selects a model
}

export function PromptSettings({ config, onUpdate, isLoading }: ConfigComponentProps) {
  // Helper to get prompt config with defaults if empty
  const getPromptConfigWithDefaults = (promptConfig?: Record<string, PromptConfig>) => {
    if (!promptConfig || Object.keys(promptConfig).length === 0) {
      return {
        legislative_review: { ...DEFAULT_PROMPT_CONFIG },
        monitor: { ...DEFAULT_PROMPT_CONFIG }
      }
    }
    return promptConfig
  }

  const [promptConfig, setPromptConfig] = useState<Record<string, PromptConfig>>(
    getPromptConfigWithDefaults(config?.prompt_config)
  )
  const [showPromptWarning, setShowPromptWarning] = useState(false)


  // Update local state when config changes (workspace switch)
  useEffect(() => {
    setPromptConfig(getPromptConfigWithDefaults(config?.prompt_config))
  }, [config?.prompt_config])

  const handlePromptConfigChange = (key: string, updates: Partial<PromptConfig>) => {
    if (!showPromptWarning) setShowPromptWarning(true)
    const newConfig = {
      ...promptConfig,
      [key]: { ...promptConfig[key], ...updates }
    }
    setPromptConfig(newConfig)

    // Update parent immediately when user changes something
    onUpdate({
      prompt_config: newConfig
    })
  }

  const renderPromptConfig = (key: string, config: PromptConfig) => {
    const displayName = key === 'legislative_review' ? 'Legislative Review' : 
                       key === 'monitor' ? 'Monitor' : 
                       key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    
    return (
      <div key={key} className="space-y-4">
        <div className="flex items-center gap-2">
          <LucideBot className="h-4 w-4" />
          <Label className="text-base font-medium">{displayName} Configuration</Label>
        </div>
        
        <div className="flex flex-col md:flex-row gap-4">
          <div className="w-full md:w-64 flex-none space-y-2">
            <div className="flex items-center gap-2">
              <LucideBot className="h-4 w-4" />
              <Label>AI Model</Label>
            </div>
            <LLMModelSelect
              app_id="legislative-review"
              value={config.model_name}
              onValueChange={(modelName) => handlePromptConfigChange(key, { model_name: modelName })}
              placeholder="Select AI model"
              disabled={isLoading}
              activeOnly={true}
              showProviderBadge={true}
              className="w-full"
            />
          </div>

          <div className="flex-grow space-y-2">
            <div className="flex items-center gap-2">
              <LucideFileText className="h-4 w-4" />
              <Label htmlFor={`${key}-prompt-textarea`} id={`${key}-prompt-label`}>Prompt</Label>
            </div>
            <textarea
              id={`${key}-prompt-textarea`}
              aria-labelledby={`${key}-prompt-label`}
              value={config.prompt_text || ''}
              onChange={(e) => handlePromptConfigChange(key, { prompt_text: e.target.value })}
              placeholder={`Enter ${displayName} prompt...`}
              className="min-h-[120px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2"
              disabled={isLoading}
            />
          </div>
        </div>
      </div>
    )
  }

  // Show loading state if configuration is still loading
  if (isLoading && Object.keys(promptConfig).length === 0) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Prompt Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">Loading configuration...</div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Prompt Configurations Card */}
      <Card>
        <CardHeader>
          <CardTitle>Prompt Configurations</CardTitle>
          <CardDescription>
            Configure prompts for different analysis types
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {Object.entries(promptConfig).map(([key, config]) => 
            renderPromptConfig(key, config)
          )}
        </CardContent>
      </Card>
    </div>
  )
}